import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ClientCompanyDataService } from '@data-services/companies';
import { ErrorHandlingService } from '@errors/error-handling.service';
import { SelectItem } from '@modals';
import { Company } from '@models/companies';
import { DataContext } from '@services/data-context';
import { MessageService } from '@services/messages';
import { CompanySelectionService } from './company-selection.service';

export const companySelectionGuard: CanActivateFn = async (route: ActivatedRouteSnapshot): Promise<UrlTree | boolean> => {
  const router = inject(Router);
  const dataContext = inject(DataContext);
  const companyDataService = inject(ClientCompanyDataService);
  const companySelectionService = inject(CompanySelectionService);
  const errorHandler = inject(ErrorHandlingService);
  const messageService = inject(MessageService);
  const translate = inject(TranslateService);

  const projectId = route.paramMap.get('projectId') ?? undefined;
  const pageSegment = route.data['companyScopedPage'] as string | undefined;

  const pageUrl = (companyId: string): UrlTree =>
    router.createUrlTree(['/projects', projectId, 'companies', companyId, pageSegment]);

  if (!projectId || !pageSegment) {
    return false;
  }

  let companies: Company[];
  try {
    companies = await companyDataService.getCompaniesAsync(projectId);
  } catch (error: unknown) {
    await errorHandler.handleErrorAsync(error, translate.instant('general.loadCompaniesError')).catch(() => undefined);
    return false;
  }

  const userId = dataContext.loggedInUser()?.id;
  const userCompanies = userId
    ? companies.filter((company) => company.Users?.some((user) => user.MBEUserId === userId || user.UserId === userId))
    : [];

  if (userCompanies.length === 1) {
    return pageUrl(userCompanies[0].Id);
  }

  const cancelResult = (): UrlTree | boolean =>
    router.routerState.snapshot.root.firstChild ? false : router.createUrlTree(['/projects']);

  if (userCompanies.length === 0) {
    messageService.showErrorMessage(translate.instant('general.noCompanyError'));
    return cancelResult();
  }

  const items: SelectItem[] = userCompanies
    .map((company): SelectItem => ({ id: company.Id, text: company.Name }))
    .sort((a, b) => a.text.localeCompare(b.text, undefined, { sensitivity: 'base' }));

  const loggedInCompanyId = dataContext.loggedInUser()?.companyId;
  const initialSelectedId = loggedInCompanyId
    ? userCompanies.find((company) => company.Id === loggedInCompanyId || company.MBECompanyId === loggedInCompanyId)?.Id
    : undefined;

  const selected = await companySelectionService.select({ companies: items, initialSelectedId });
  if (!selected) {
    return cancelResult();
  }

  return pageUrl(String(selected.id));
};
